import { type RateRow, RatesPanel } from "@/components/rates/rates-panel";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { DiscordLinkField } from "@/components/users/discord-link-field";
import { SecondaryEmails } from "@/components/users/secondary-emails";
import { ApiError, api } from "@/lib/api";
import { formatExample } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { formatHours } from "@/lib/utils";
import type { GlobalRole } from "@prisma/client";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/users/:id` — admin-only user detail + edit. Server gates the
 * endpoint (403 -> redirect to /my). The edit form patches
 * globalRole + IBAN via `PATCH /api/users/:id`, toasts, and
 * invalidates the route.
 *
 * `GLOBAL_ROLE_LABEL` / `ALL_GLOBAL_ROLES` are inlined here rather
 * than porting the whole server-coupled `permissions.ts` — they're
 * two tiny constants.
 */

const ALL_GLOBAL_ROLES: GlobalRole[] = ["admin", "accountant"];

const globalRoleVariant: Record<GlobalRole, "default" | "secondary" | "outline" | "destructive"> = {
  admin: "destructive",
  accountant: "default",
};

const approvalVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  worked: "outline",
  approved: "success",
  paid: "default",
  rejected: "destructive",
};

const billingVariant: Record<string, "default" | "secondary" | "success" | "warning" | "outline"> =
  {
    unbilled: "outline",
    to_invoice: "warning",
    invoiced: "success",
    non_billable: "secondary",
  };

type UserDetail = {
  id: string;
  name: string | null;
  email: string;
  globalRole: GlobalRole | null;
  gitlabUserId: string | null;
  discordUsername: string | null;
  /** Set only when the bot resolved the handle to a real Discord account —
   *  that link, not the handle, is what a meeting is matched against. */
  discordUserId: string | null;
  iban: string | null;
  /** Chosen UI/e-mail language; null = never chosen. */
  locale: string | null;
  /** Chosen date/number format; null = follows the language. */
  formatLocale: string | null;
  /** Set by offboarding: the account can't sign in until enabled again. */
  disabledAt: string | null;
  createdAt: string;
  timeEntries: Array<{
    id: string;
    hours: string;
    spentOn: string;
    approvalState: string;
    billingState: string;
    /** Null for untriaged / external Clockify imports — the row has
     *  no Hub project bound. `externalProjectName` keeps the
     *  Clockify-side name for display in that case. */
    project: { name: string } | null;
    externalProjectName: string | null;
    workItem: { title: string } | null;
  }>;
};

type UserDetailLoaderData = UserDetail & {
  /** Cost rates for this user. The `/users/:id` route is already
   *  admin-only, so `/api/rates` always answers — no role gate
   *  needed at this layer. */
  costRates: RateRow[];
};

export const Route = createFileRoute("/_app/users/$id")({
  loader: async ({ params }): Promise<UserDetailLoaderData> => {
    try {
      const [user, rates] = await Promise.all([
        api.get<UserDetail>(`/api/users/${params.id}`),
        api.get<{ rates: RateRow[] }>(
          `/api/rates?type=cost&userId=${encodeURIComponent(params.id)}&pageSize=200`,
        ),
      ]);
      return { ...user, costRates: rates.rates };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: UserDetailPage,
});

function UserDetailPage() {
  const { t } = useTranslation();
  const user = Route.useLoaderData();

  // Client-sorted via the columns' `sortAccessor`s — no sort prop.
  const entryColumns: Array<DataTableColumn<UserDetail["timeEntries"][number]>> = [
    {
      key: "project",
      header: t("usr.detail.columns.project"),
      sortAccessor: (te) => te.project?.name ?? te.externalProjectName ?? "",
      cellClassName: "font-medium",
      cell: (te) =>
        te.project ? (
          te.project.name
        ) : te.externalProjectName ? (
          <span className="italic text-muted-foreground">
            {t("usr.detail.clockify", { name: te.externalProjectName })}
          </span>
        ) : (
          <span className="italic text-muted-foreground">{t("usr.detail.noProject")}</span>
        ),
    },
    {
      key: "workItem",
      header: t("usr.detail.columns.workItem"),
      sortAccessor: (te) => te.workItem?.title,
      cellClassName: "text-sm text-muted-foreground max-w-[200px] truncate",
      cell: (te) =>
        te.workItem ? (
          te.workItem.title
        ) : (
          <span className="italic">{t("usr.detail.overhead")}</span>
        ),
    },
    {
      key: "hours",
      header: t("usr.detail.columns.hours"),
      sortAccessor: (te) => Number(te.hours),
      cell: (te) => formatHours(Number(te.hours)),
    },
    {
      key: "spentOn",
      header: t("usr.detail.columns.date"),
      sortAccessor: (te) => te.spentOn,
      cellClassName: "text-sm text-muted-foreground",
      cell: (te) => <LocalDate value={te.spentOn} />,
    },
    {
      key: "approval",
      header: t("usr.detail.columns.approval"),
      sortAccessor: (te) => te.approvalState,
      cell: (te) => (
        <Badge variant={approvalVariant[te.approvalState] ?? "outline"}>
          {t(`usr.approval.${te.approvalState}`, { defaultValue: te.approvalState })}
        </Badge>
      ),
    },
    {
      key: "billing",
      header: t("usr.detail.columns.billing"),
      sortAccessor: (te) => te.billingState,
      cell: (te) => (
        <Badge variant={billingVariant[te.billingState] ?? "outline"}>
          {t(`usr.billing.${te.billingState}`, { defaultValue: te.billingState })}
        </Badge>
      ),
    },
  ];

  const maskedIban = user.iban
    ? `${user.iban.slice(0, 4)}${"*".repeat(Math.max(0, user.iban.length - 8))}${user.iban.slice(-4)}`
    : "---";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">
            {user.name}
            {user.disabledAt && (
              <Badge variant="destructive" className="ml-3 align-middle">
                {t("usr.disabled.badge")}
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {!user.gitlabUserId && !user.globalRole && !user.disabledAt && (
            <SendSignInLink userId={user.id} />
          )}
          {user.disabledAt ? (
            <EnableButton userId={user.id} />
          ) : (
            <>
              <DisableAccessButton userId={user.id} name={user.name ?? user.email} />
              <OffboardButton userId={user.id} name={user.name ?? user.email} />
            </>
          )}
          <Button variant="outline" asChild>
            <AppLink href="/users">{t("usr.detail.backToUsers")}</AppLink>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("usr.detail.globalRole")}</p>
            <p className="mt-1">
              {user.globalRole ? (
                <Badge variant={globalRoleVariant[user.globalRole]}>
                  {t(`usr.role.${user.globalRole}`)}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">—</span>
              )}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">GitLab ID</p>
            <p className="font-mono text-lg font-semibold">{user.gitlabUserId ?? "---"}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("usr.detail.discord")}</p>
            <p className="font-mono text-sm font-semibold" data-testid="discord-username">
              {user.discordUsername ?? "---"}
            </p>
            {/* A handle on its own does nothing — only a resolved account id
                lets a meeting propose time for this person. Stated in full on
                the Meeting identity card below; here it's just the glance. */}
            {user.discordUsername && (
              <p className="mt-1 text-xs">
                {user.discordUserId ? (
                  <span className="text-green-600">{t("usr.detail.linked")}</span>
                ) : (
                  <span className="text-destructive">{t("usr.detail.notLinked")}</span>
                )}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">IBAN</p>
            <p className="font-mono text-sm font-semibold">{maskedIban}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("usr.detail.editUser")}</CardTitle>
          <CardDescription>
            <Trans
              i18nKey="usr.detail.editDescription"
              components={{ date: <LocalDate value={user.createdAt} /> }}
            />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <EditUserForm
            userId={user.id}
            initialRole={user.globalRole}
            initialIban={user.iban}
            initialLocale={user.locale}
            initialFormatLocale={user.formatLocale}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("usr.detail.meetingTitle")}</CardTitle>
          <CardDescription>{t("usr.detail.meetingDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <DiscordLinkField
            userId={user.id}
            initialHandle={user.discordUsername}
            initialLinked={Boolean(user.discordUserId)}
          />
          <div className="border-t pt-4">
            <SecondaryEmails userId={user.id} primaryEmail={user.email} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("usr.detail.timeEntries")}</CardTitle>
          <CardDescription>
            {t("usr.detail.timeEntriesCount", { count: user.timeEntries.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={entryColumns}
            rows={user.timeEntries}
            rowKey={(te) => te.id}
            emptyState={t("usr.detail.timeEntriesEmpty")}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("usr.detail.costRates")}</CardTitle>
          <CardDescription>{t("usr.detail.costRatesDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <RatesPanel
            title=""
            type="cost"
            rates={user.costRates}
            anchor={{ kind: "user", userId: user.id, userName: user.name ?? user.email }}
          />
        </CardContent>
      </Card>
    </div>
  );
}

/** A client without GitLab signs in by an e-mailed link; this mails a fresh one. */
function SendSignInLink({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const send = useApiMutation(
    () => api.post<{ invited: boolean; inviteError?: string }>(`/api/users/${userId}/invite`, {}),
    {
      onSuccess: ({ invited, inviteError }) => {
        toast(
          invited
            ? { title: t("usr.signIn.sent"), variant: "success" }
            : {
                title: t("usr.signIn.notSent"),
                description: inviteError ?? "",
                variant: "destructive",
              },
        );
      },
      onError: (message) =>
        toast({ title: t("usr.signIn.failed"), description: message, variant: "destructive" }),
    },
  );
  return (
    <Button variant="outline" disabled={send.pending} onClick={() => send.mutate()}>
      {t("usr.signIn.send")}
    </Button>
  );
}

type OffboardResult = {
  removedFrom: string[];
  failed: Array<{ project: string; error: string }>;
  managerTakenOverBy: Array<{ project: string; userId: string }>;
  workItemsReassigned: number;
  watchesDropped: number;
  sessionsEnded: number;
  tokensRevoked: number;
};

/** Someone left: out of every project and open work item, signed out of Hub. */
function OffboardButton({ userId, name }: { userId: string; name: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const offboard = useApiMutation(
    () => api.post<OffboardResult>(`/api/users/${userId}/offboard`, {}),
    {
      onSuccess: (r) => {
        router.invalidate();
        const taken = r.managerTakenOverBy.map((m) => m.project);
        toast({
          title: t("usr.offboard.title", {
            projects: r.removedFrom.length,
            items: r.workItemsReassigned,
          }),
          description: [
            r.failed.length
              ? t("usr.offboard.notRemoved", {
                  list: r.failed.map((f) => `${f.project} (${f.error})`).join(", "),
                })
              : "",
            taken.length ? t("usr.offboard.manager", { list: taken.join(", ") }) : "",
            t("usr.offboard.signedOut", { sessions: r.sessionsEnded, tokens: r.tokensRevoked }),
          ]
            .filter(Boolean)
            .join(" · "),
          variant: r.failed.length ? "destructive" : "success",
        });
      },
      onError: (message) =>
        toast({ title: t("usr.offboard.failed"), description: message, variant: "destructive" }),
    },
  );
  return (
    <Button
      variant="destructive"
      disabled={offboard.pending}
      onClick={() => {
        if (window.confirm(t("usr.offboard.confirm", { name }))) offboard.mutate();
      }}
    >
      {offboard.pending ? (
        <LoadingLabel text={t("usr.offboard.removing")} />
      ) : (
        t("usr.offboard.button")
      )}
    </Button>
  );
}

/** Hub access off, projects and GitLab untouched: for people who work only in GitLab. */
function DisableAccessButton({ userId, name }: { userId: string; name: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const disable = useApiMutation(() => api.post(`/api/users/${userId}/disable`, {}), {
    onSuccess: () => {
      toast({ title: t("usr.disableAccess.done"), variant: "success" });
      router.invalidate();
    },
    onError: (message) =>
      toast({ title: t("usr.disableAccess.failed"), description: message, variant: "destructive" }),
  });
  return (
    <Button
      variant="outline"
      disabled={disable.pending}
      onClick={() => {
        if (window.confirm(t("usr.disableAccess.confirm", { name }))) disable.mutate();
      }}
    >
      {disable.pending ? (
        <LoadingLabel text={t("usr.disableAccess.disabling")} />
      ) : (
        t("usr.disableAccess.button")
      )}
    </Button>
  );
}

/** Switches an offboarded account back on; projects and roles are granted again by hand. */
function EnableButton({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const enable = useApiMutation(() => api.post(`/api/users/${userId}/enable`, {}), {
    onSuccess: () => {
      toast({ title: t("usr.disabled.enabled"), variant: "success" });
      router.invalidate();
    },
    onError: (message) =>
      toast({ title: t("usr.disabled.failed"), description: message, variant: "destructive" }),
  });
  return (
    <Button variant="outline" disabled={enable.pending} onClick={() => enable.mutate()}>
      {enable.pending ? (
        <LoadingLabel text={t("usr.disabled.enabling")} />
      ) : (
        t("usr.disabled.enable")
      )}
    </Button>
  );
}

function EditUserForm({
  userId,
  initialRole,
  initialIban,
  initialLocale,
  initialFormatLocale,
}: {
  userId: string;
  initialRole: GlobalRole | null;
  initialIban: string | null;
  initialLocale: string | null;
  initialFormatLocale: string | null;
}) {
  const { t } = useTranslation();
  const router = useRouter();
  const [locale, setLocale] = useState(initialLocale ?? "");
  const [formatLocale, setFormatLocale] = useState(initialFormatLocale ?? "");
  const [role, setRole] = useState<string>(initialRole ?? "");
  const [iban, setIban] = useState(initialIban ?? "");
  const update = useApiMutation(
    (body: {
      globalRole: string | null;
      iban: string | null;
      locale: string | null;
      formatLocale: string | null;
    }) => api.patch(`/api/users/${userId}`, body),
    {
      onSuccess: () => {
        toast({ title: t("usr.edit.updated"), variant: "success" });
        router.invalidate();
      },
      errorMessage: (err) => (err instanceof Error ? err.message : t("usr.edit.error")),
      onError: (msg) =>
        toast({ title: t("usr.edit.updateFailed"), description: msg, variant: "destructive" }),
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (update.pending) return;
    await update.mutate({
      globalRole: role === "" ? null : role,
      iban: iban.trim() || null,
      locale: locale || null,
      formatLocale: formatLocale || null,
    });
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="globalRole" className="text-sm font-medium leading-none">
          {t("usr.edit.globalRole")}
        </label>
        <Select
          className="w-full"
          aria-label={t("usr.edit.globalRole")}
          value={role}
          onChange={setRole}
          options={[
            { value: "", label: t("usr.edit.noneRole") },
            ...ALL_GLOBAL_ROLES.map((r) => ({ value: r, label: t(`usr.role.${r}`) })),
          ]}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="iban" className="text-sm font-medium leading-none">
          IBAN
        </label>
        <input
          id="iban"
          type="text"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder={t("usr.edit.ibanPlaceholder")}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="locale" className="text-sm font-medium leading-none">
          {t("userSettings.language")}
        </label>
        <Select
          className="w-full"
          aria-label={t("userSettings.language")}
          data-testid="user-locale"
          value={locale}
          onChange={setLocale}
          options={[
            { value: "", label: t("userSettings.notSet") },
            { value: "cs", label: t("langFormat.languageCs") },
            { value: "en", label: t("langFormat.languageEn") },
          ]}
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="formatLocale" className="text-sm font-medium leading-none">
          {t("userSettings.format")}
        </label>
        <Select
          className="w-full"
          aria-label={t("userSettings.format")}
          data-testid="user-format-locale"
          value={formatLocale}
          onChange={setFormatLocale}
          options={[
            { value: "", label: t("userSettings.followLanguage") },
            { value: "cs", label: t("langFormat.formatCs", { example: formatExample("cs") }) },
            { value: "en", label: t("langFormat.formatEn", { example: formatExample("en") }) },
          ]}
        />
      </div>

      <Button type="submit" disabled={update.pending}>
        {update.pending ? <LoadingLabel text={t("usr.edit.saving")} /> : t("usr.edit.save")}
      </Button>
    </form>
  );
}
